import React, { useState } from 'react';
import Grid from '@mui/material/Grid2';
import { Typography, Chip, Box, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FallbackTypography from 'src/components/shared/FallbackTypography';
import { formatNumericValue, getChipColor } from 'src/utils/common';

const EditableValueView = ({ title, value, onEdit, disabled, readOnly, bold, titleValueGap = 1, isNumeric = false, decimalPlaces = 2 }) => {
  const hasValue = Boolean(value && value.trim());
  const [showAll, setShowAll] = useState(false);
  
  const confirmationNumbers = hasValue
    ? value.split(',').map(num => {
      const trimmed = num.trim();
      return isNumeric ? formatNumericValue(trimmed, isNumeric, decimalPlaces) : trimmed;
    }).filter(Boolean)
    : [];

  const hasMoreNumbers = confirmationNumbers.length > 1;
  const visibleNumbers = hasMoreNumbers && !showAll ? confirmationNumbers.slice(0, 1) : confirmationNumbers;
  const hiddenCount = confirmationNumbers.length - 1;

  const handleToggle = () => {
    setShowAll(!showAll);
  };

  return (
    <Grid item>
      <Box display="flex" alignItems="flex-start" justifyContent="start" mb={1}>
        <Typography
          variant="body2"
          fontWeight={bold ? 'bold' : 'normal'}
          sx={{ mr: titleValueGap, mt: 0.5, flexShrink: 0 }}
        >
          {title}
        </Typography>

        {!disabled && !readOnly && (
          <Box
            ml={0}
            sx={{
              display: 'inline-flex',
              cursor: 'pointer',
              color: 'primary.main',
              flexShrink: 0,
              mt: 0.25,
              '&:hover': {
                color: 'secondary.main',
              },
            }}
            onClick={onEdit}
          >
            <EditIcon fontSize="small" />
          </Box>
        )}

        <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" sx={{ flex: 1 }}>
          {hasValue ? (
            <>
              {visibleNumbers.map((num, idx) => (
                <Tooltip key={idx} title={num} placement="top" arrow>
                  <Chip
                    label={num}
                    size="small"
                    color={getChipColor(num)}
                    variant="filled"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      maxWidth: '150px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }
                    }}
                  />
                </Tooltip>
              ))}

              {hasMoreNumbers && (
                <Tooltip title={showAll ? "Click to show less" : `Click to show all ${confirmationNumbers.length} numbers`} placement="top" arrow>
                  <Chip
                    label={showAll ? "Show less" : `+${hiddenCount} more`}
                    size="small"
                    color="default"
                    variant="outlined"
                    onClick={handleToggle}
                    icon={showAll ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  />
                </Tooltip>
              )}
            </>
          ) : (
            <Chip
              label={
                <FallbackTypography
                  value={value}
                  fallback={disabled ? "N/A" : "Enter Confirmation No"}
                  color="primary"
                  variant="body2"
                  fontWeight="bold"
                />
              }
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
            />
          )}
        </Box>
      </Box>
    </Grid>
  );
};

export default EditableValueView;